import { z } from 'zod';

export const DEFAULT_BRAND_RADIUS = '0.625';
export const MIN_BRAND_RADIUS = 0;
export const MAX_BRAND_RADIUS = 1.5;

const radiusInputSchema = z.union([z.number(), z.string()]);

function parseRadius(value: string | number): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const normalized = value.trim().toLowerCase();
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:rem)?$/.test(normalized)) {
    return null;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatRadius(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}

/**
 * Canonical company radius stored in settings: a unitless rem value bounded
 * to the choices supported by the Branding screen. Legacy values such as
 * `0.75rem` remain readable, while malformed CSS never reaches the document.
 */
export const brandRadiusSchema = radiusInputSchema.transform((value, ctx) => {
  const parsed = parseRadius(value);
  if (parsed === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'Invalid brand radius',
    });
    return z.NEVER;
  }

  return formatRadius(
    Math.min(MAX_BRAND_RADIUS, Math.max(MIN_BRAND_RADIUS, parsed)),
  );
});

export function normalizeBrandRadius(value: unknown): string {
  const result = brandRadiusSchema.safeParse(value);
  return result.success ? result.data : DEFAULT_BRAND_RADIUS;
}

export function brandRadiusCssValue(value: unknown): string {
  return `${normalizeBrandRadius(value)}rem`;
}
